import { useState, useMemo } from 'react';
import { Plus, Trash2, Loader2, Users, UserX, Share2, Pencil, Pin, PinOff } from 'lucide-react';
import { useCollections, Collection } from '@/hooks/useCollections';
import { useMultipleCollectionFollowerProfiles } from '@/hooks/useCollectionFollowerProfiles';
import { useCollectionsUnreadActivity } from '@/hooks/useCollectionsUnreadActivity';
import { CreateCollectionSheet } from './CreateCollectionSheet';
import { EditCollectionSheet } from './EditCollectionSheet';
import { FollowedCollectionsSection } from './FollowedCollectionsSection';
import { CollectionGridCard } from './CollectionGridCard';
import { CollectionGridSkeleton } from '@/components/skeletons';
import { CollectionsEmptyState } from '@/components/empty-states/CollectionsEmptyState';
import { toast } from 'sonner';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import { ShareSheet } from '@/components/share/ShareSheet';

export type CollectionFilter = 'mine' | 'following';
export type CollectionTypeFilter = 'all' | 'private' | 'collaborative' | 'public';

// Combined filter that includes both ownership and type
export type CombinedCollectionFilter = 'all' | 'mine' | 'mine-private' | 'mine-collaborative' | 'mine-public' | 'following';

interface CollectionsListProps {
  onSelectCollection: (collection: Collection) => void;
  filter: CombinedCollectionFilter;
}

export function CollectionsList({ onSelectCollection, filter }: CollectionsListProps) {
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [pinningId, setPinningId] = useState<string | null>(null);
  const [longPressCollection, setLongPressCollection] = useState<Collection | null>(null);
  const [shareCollection, setShareCollection] = useState<Collection | null>(null);
  const { collections, isLoading, deleteCollection, updateCollection, pinCollection, unpinCollection } = useCollections();
  
  // Get collaborative collection IDs to fetch followers and activity for
  const collaborativeCollectionIds = useMemo(() => 
    collections.filter(c => c.is_collaborative).map(c => c.id),
    [collections]
  );
  
  // Fetch follower profiles for all collaborative collections
  const { followersMap } = useMultipleCollectionFollowerProfiles(collaborativeCollectionIds);
  
  // Fetch unread activity for collaborative collections
  const { activityMap } = useCollectionsUnreadActivity(collaborativeCollectionIds);
  
  // Filter collections based on combined filter
  const filteredCollections = useMemo(() => {
    if (filter === 'all' || filter === 'mine') return collections;
    if (filter === 'mine-private') return collections.filter(c => !c.is_collaborative && !c.is_public);
    if (filter === 'mine-collaborative') return collections.filter(c => c.is_collaborative);
    if (filter === 'mine-public') return collections.filter(c => c.is_public);
    return collections;
  }, [collections, filter]);

  const handleDelete = async (collection: Collection) => {
    setDeletingId(collection.id);
    try {
      await deleteCollection.mutateAsync(collection.id);
      // Deleted silently
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete collection');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleCollaborative = async (collection: Collection) => {
    setTogglingId(collection.id);
    try {
      await updateCollection.mutateAsync({
        id: collection.id,
        is_collaborative: !collection.is_collaborative,
      });
      // Updated silently
    } catch (error) {
      console.error('Toggle collaborative error:', error);
      toast.error('Failed to update collection');
    } finally {
      setTogglingId(null);
    }
  };

  const handleTogglePin = async (collection: Collection) => {
    setPinningId(collection.id);
    try {
      if (collection.pinned_at) {
        await unpinCollection.mutateAsync(collection.id);
        // Unpinned silently
      } else {
        await pinCollection.mutateAsync(collection.id);
        // Pinned silently
      }
    } catch (error) {
      console.error('Pin toggle error:', error);
      toast.error('Failed to update collection');
    } finally {
      setPinningId(null);
    }
  };

  // Hide the create card once user has more than 2 collections (they can use the FAB)
  const showCreateCard = collections.length <= 2;

  if (isLoading && (filter === 'mine' || filter === 'all')) {
    return <CollectionGridSkeleton count={5} showCreateCard={showCreateCard} />;
  }

  return (
    <>
      <div className="space-y-4">
        {/* Filtered Content */}
        {filter !== 'following' ? (
          // My Collections (+ followed if "all") - 2 column grid
          collections.length === 0 && !isLoading ? (
            <CollectionsEmptyState onCreateCollection={() => setShowCreateSheet(true)} />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {/* Create New Collection Card - only show for new users */}
                {showCreateCard && (
                  <button
                    onClick={() => setShowCreateSheet(true)}
                    className="aspect-[3/4] rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 flex flex-col items-center justify-center gap-3 active:bg-primary/10 transition-colors"
                  >
                    <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                      <Plus className="w-7 h-7 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-primary">New Collection</span>
                  </button>
                )}

                {/* Collection Grid Cards */}
                {filteredCollections.map((collection) => (
                  <CollectionGridCard
                    key={collection.id}
                    collection={collection}
                    onSelect={onSelectCollection}
                    onLongPress={setLongPressCollection}
                    followers={followersMap[collection.id] || []}
                    activity={activityMap[collection.id] ? {
                      unreadMessages: activityMap[collection.id].unreadMessages,
                      newItemsCount: activityMap[collection.id].newItemsCount,
                    } : undefined}
                  />
                ))}
              </div>

              {/* Show followed collections below when "all" filter is active */}
              {filter === 'all' && (
                <div className="mt-6">
                  <FollowedCollectionsSection onSelectCollection={onSelectCollection} showHeader={true} />
                </div>
              )}
            </>
          )
        ) : (
          // Following only
          <FollowedCollectionsSection onSelectCollection={onSelectCollection} showHeader={false} />
        )}
      </div>

      <CreateCollectionSheet
        open={showCreateSheet}
        onOpenChange={setShowCreateSheet}
      />
      
      <EditCollectionSheet
        open={!!editingCollection}
        onOpenChange={(open) => !open && setEditingCollection(null)}
        collection={editingCollection}
      />

      {/* Long Press Actions Sheet */}
      <DraggableBottomSheet
        open={!!longPressCollection}
        onOpenChange={(open) => !open && setLongPressCollection(null)}
        title={longPressCollection?.name || 'Collection Options'}
        height="auto"
      >
        {longPressCollection && (
          <div className="space-y-1 pb-6">
            <button
              onClick={() => {
                setEditingCollection(longPressCollection);
                setLongPressCollection(null);
              }}
              className="w-full flex items-center gap-3 p-4 rounded-xl active:bg-surface-3 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Pencil className="w-5 h-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-medium text-foreground">Edit Collection</p>
                <p className="text-sm text-muted-foreground">Change name, color or description</p>
              </div>
            </button>

            <button
              onClick={() => {
                const col = longPressCollection;
                setLongPressCollection(null);
                // Delay to let parent sheet close before opening share sheet
                setTimeout(() => setShareCollection(col), 300);
              }}
              className="w-full flex items-center gap-3 p-4 rounded-xl active:bg-surface-3 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Share2 className="w-5 h-5 text-blue-500" />
              </div>
              <div className="text-left">
                <p className="font-medium text-foreground">Share Collection</p>
                <p className="text-sm text-muted-foreground">Send to friends or copy link</p>
              </div>
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleTogglePin(longPressCollection);
                setLongPressCollection(null);
              }}
              disabled={pinningId === longPressCollection.id}
              className="w-full flex items-center gap-3 p-4 rounded-xl active:bg-surface-3 transition-colors disabled:opacity-50"
            >
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                {longPressCollection.pinned_at ? (
                  <PinOff className="w-5 h-5 text-amber-500" />
                ) : (
                  <Pin className="w-5 h-5 text-amber-500" />
                )}
              </div>
              <div className="text-left">
                <p className="font-medium text-foreground">
                  {longPressCollection.pinned_at ? 'Unpin Collection' : 'Pin to Top'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {longPressCollection.pinned_at ? 'Remove from top of list' : 'Keep at top of your collections'}
                </p>
              </div>
            </button>

            <button
              onClick={() => {
                handleToggleCollaborative(longPressCollection);
                setLongPressCollection(null);
              }}
              disabled={togglingId === longPressCollection.id}
              className="w-full flex items-center gap-3 p-4 rounded-xl active:bg-surface-3 transition-colors disabled:opacity-50"
            >
              <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                {longPressCollection.is_collaborative ? (
                  <UserX className="w-5 h-5 text-green-500" />
                ) : (
                  <Users className="w-5 h-5 text-green-500" />
                )}
              </div>
              <div className="text-left">
                <p className="font-medium text-foreground">
                  {longPressCollection.is_collaborative ? 'Disable Collaboration' : 'Enable Collaboration'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {longPressCollection.is_collaborative 
                    ? 'Only you can add items' 
                    : 'Let others add items to this collection'}
                </p>
              </div>
            </button>

            <div className="pt-2 border-t border-border mt-2">
              <button
                onClick={() => {
                  handleDelete(longPressCollection);
                  setLongPressCollection(null);
                }}
                disabled={deletingId === longPressCollection.id}
                className="w-full flex items-center gap-3 p-4 rounded-xl active:bg-destructive/10 transition-colors disabled:opacity-50"
              >
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  {deletingId === longPressCollection.id ? (
                    <Loader2 className="w-5 h-5 text-destructive animate-spin" />
                  ) : (
                    <Trash2 className="w-5 h-5 text-destructive" />
                  )}
                </div>
                <div className="text-left">
                  <p className="font-medium text-destructive">Delete Collection</p>
                  <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                </div>
              </button>
            </div>
          </div>
        )}
      </DraggableBottomSheet>

      {/* Share sheet - rendered outside long-press sheet so it persists */}
      {shareCollection && (
        <ShareSheet
          title={shareCollection.name}
          text={shareCollection.description || `Check out my collection: ${shareCollection.name}`}
          imageUrl={shareCollection.cover_image_url || shareCollection.preview_images?.[0]}
          collectionId={shareCollection.id}
          open={!!shareCollection}
          onOpenChange={(open) => { if (!open) setShareCollection(null); }}
        />
      )}
    </>
  );
}

